/**
 * Pay Invoice — Shieldz Payments deep-link landing screen.
 *
 * Opened when the wallet boots with `start_param = pay_<public_id>` (set by
 * the hosted checkout page's "Pay with Shieldz" deep link, which routes
 * through https://t.me/shieldz_wallet_bot?startapp=pay_<public_id>).
 *
 * Flow:
 *   1. Fetch the invoice metadata (merchant, amount).
 *   2. Generate a fresh quote with the user's local t-address as the
 *      refund target (MAYAChain's `origin` field).
 *   3. Render the deposit details (MAYA t-vault + memo + ZEC amount).
 *   4. Two completion paths:
 *        - "Continue in Send" → routes to the swap screen with prefill
 *          (handled by parent: this screen exposes the values via callback)
 *        - "I've already paid" → POST /api/v1/invoices/:id/notify and
 *          render a "watching" state.
 *
 * Edge cases handled:
 *   - Invoice not found → friendly 404
 *   - Already paid → success card with settlement metadata
 *   - Expired → "ask the merchant for a fresh link"
 *   - Failed (MAYA refund) → tell the user their refund t-addr received it
 *   - MAYAChain halted → service-unavailable card
 *
 * What this screen does NOT do (deferred to a follow-up):
 *   - Auto-fill the wallet's Swap screen and broadcast in one tap. The V1
 *     experience shows the deposit details and asks the user to confirm
 *     via the standard Swap flow. The "Continue in Send" button is the
 *     scaffolding for the deeper integration.
 */

import { useCallback, useEffect, useState } from "react";
import { useUnlockedWallet } from "../wallet";
import {
  fetchPaymentInvoice,
  createInvoiceQuote,
  notifyInvoicePayment,
  TeleswapBotError,
  type PaymentInvoice,
  type PaymentInvoiceQuote,
} from "../lib/teleswap-client";
import { atomicToHuman } from "../lib/atomic-units";
import { TgScreen } from "../components/TgScreen";

type Stage =
  | { kind: "loading" }
  | { kind: "invalid_taddr"; reason: string }
  | { kind: "halt" }
  | { kind: "error"; message: string }
  | { kind: "fetched"; invoice: PaymentInvoice; quote: PaymentInvoiceQuote }
  | { kind: "notified"; invoice: PaymentInvoice; txHash: string }
  | { kind: "terminal"; invoice: PaymentInvoice };

interface PayInvoiceScreenProps {
  publicId: string;
  onBack: () => void;
}

const formatUsd = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

const isHex64 = (s: string): boolean => /^[0-9a-f]{64}$/i.test(s);

export function PayInvoiceScreen({ publicId, onBack }: PayInvoiceScreenProps) {
  const wallet = useUnlockedWallet();
  const [stage, setStage] = useState<Stage>({ kind: "loading" });
  const [txHashInput, setTxHashInput] = useState("");

  const loadAndQuote = useCallback(async () => {
    if (!wallet) return;
    const taddr = wallet.addresses.zecTransparent;
    if (!taddr || !taddr.startsWith("t1")) {
      setStage({
        kind: "invalid_taddr",
        reason: "Your wallet hasn't derived a transparent t1 address yet — try reloading.",
      });
      return;
    }
    try {
      const invoice = await fetchPaymentInvoice(publicId);
      if (invoice.status !== "pending") {
        setStage({ kind: "terminal", invoice });
        return;
      }
      // Reuse a live quote if one exists for this t-addr; otherwise mint one.
      let quote = invoice.quote;
      if (
        !quote ||
        quote.expires_at_ms <= Date.now()
      ) {
        quote = await createInvoiceQuote(publicId, taddr);
      }
      setStage({ kind: "fetched", invoice, quote });
    } catch (e) {
      if (e instanceof TeleswapBotError && e.status === 503) {
        setStage({ kind: "halt" });
        return;
      }
      setStage({
        kind: "error",
        message: e instanceof Error ? e.message : String(e),
      });
    }
  }, [publicId, wallet]);

  useEffect(() => {
    void loadAndQuote();
  }, [loadAndQuote]);

  const handleNotify = useCallback(async () => {
    if (stage.kind !== "fetched") return;
    const txHash = txHashInput.trim().toLowerCase();
    if (!isHex64(txHash)) return;
    try {
      await notifyInvoicePayment(publicId, txHash);
      setStage({ kind: "notified", invoice: stage.invoice, txHash });
    } catch (e) {
      setStage({
        kind: "error",
        message: e instanceof Error ? e.message : String(e),
      });
    }
  }, [publicId, stage, txHashInput]);

  return (
    <TgScreen title="Pay invoice" onClose={onBack}>
      <div style={{ padding: "12px 16px 24px" }}>
        {stage.kind === "loading" && (
          <div style={muted}>Loading invoice…</div>
        )}

        {stage.kind === "invalid_taddr" && (
          <Card>
            <Heading>Wallet not ready</Heading>
            <p style={muted}>{stage.reason}</p>
          </Card>
        )}

        {stage.kind === "halt" && (
          <Card>
            <Heading>MAYAChain is temporarily paused</Heading>
            <p style={muted}>
              The swap network we route through is in a halt window. Try again
              in a few minutes — payments will resume as soon as it does.
            </p>
          </Card>
        )}

        {stage.kind === "error" && (
          <Card>
            <Heading>Something went wrong</Heading>
            <p style={muted}>{stage.message}</p>
            <button onClick={() => void loadAndQuote()} style={btn}>
              Try again
            </button>
          </Card>
        )}

        {stage.kind === "terminal" && (
          <Card>
            <Heading>
              {stage.invoice.status === "paid"
                ? "Already paid"
                : stage.invoice.status === "expired"
                  ? "Invoice expired"
                  : "Payment failed"}
            </Heading>
            <Row k="Amount" v={formatUsd(stage.invoice.amount_usd_cents)} />
            {stage.invoice.memo && (
              <Row k="Memo" v={stage.invoice.memo} />
            )}
            {stage.invoice.status === "failed" && (
              <p style={muted}>
                MAYAChain returned your ZEC to your refund address. Ask the
                merchant for a fresh invoice and try again.
              </p>
            )}
            {stage.invoice.status === "expired" && (
              <p style={muted}>
                The checkout window has closed. Ask the merchant to issue a
                new invoice link.
              </p>
            )}
          </Card>
        )}

        {stage.kind === "fetched" && (
          <>
            <Card>
              <div style={{ fontSize: 28, fontWeight: 700 }}>
                {formatUsd(stage.invoice.amount_usd_cents)}
              </div>
              <div style={muted}>
                ≈ {atomicToHuman(stage.quote.amount_zec_atomic, 8)} ZEC paid
                privately
              </div>
            </Card>

            <Card>
              <Row k="Invoice" v={stage.invoice.id} />
              {stage.invoice.memo && (
                <Row k="Memo" v={stage.invoice.memo} />
              )}
              <Row
                k="Merchant receives"
                v={`${atomicToHuman(stage.quote.expected_out_atomic, 6)} ${stage.invoice.settlement.chain} USDC`}
              />
            </Card>

            <Card>
              <Heading>Deposit details</Heading>
              <Row k="Address" v={stage.quote.deposit_address} mono />
              <Row k="Memo" v={stage.quote.deposit_memo} mono />
              <Row
                k="Amount"
                v={`${atomicToHuman(stage.quote.amount_zec_atomic, 8)} ZEC`}
                mono
              />
              <p style={{ ...muted, marginTop: 8 }}>
                Open Swap, paste these values, broadcast. Then come back here
                and submit your tx hash so we can track it.
              </p>
            </Card>

            <Card>
              <Heading>Already paid?</Heading>
              <p style={{ ...muted, marginBottom: 8 }}>
                Paste your Zcash transaction hash so we can track settlement.
              </p>
              <input
                type="text"
                value={txHashInput}
                onChange={(e) =>
                  setTxHashInput(e.target.value.trim().toLowerCase())
                }
                placeholder="64 hex chars"
                style={input}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
              />
              <button
                onClick={() => void handleNotify()}
                style={btn}
                disabled={!isHex64(txHashInput)}
              >
                Submit tx hash
              </button>
            </Card>
          </>
        )}

        {stage.kind === "notified" && (
          <Card>
            <Heading>Watching your payment…</Heading>
            <Row k="Zcash tx" v={stage.txHash} mono />
            <p style={muted}>
              Typical settlement takes ~2–3 minutes after the Zcash block
              lands. Your refund t-address will receive the ZEC back if the
              swap fails (rare).
            </p>
          </Card>
        )}
      </div>
    </TgScreen>
  );
}

// ─── Inline styles (tiny — match the wallet's existing look) ──────────

const Card = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      background: "var(--bg-elev)",
      border: "1px solid rgba(255,255,255,0.06)",
      borderRadius: 12,
      padding: 16,
      marginBottom: 10,
    }}
  >
    {children}
  </div>
);

const Heading = ({ children }: { children: React.ReactNode }) => (
  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
    {children}
  </div>
);

const Row = ({
  k,
  v,
  mono,
}: {
  k: string;
  v: string;
  mono?: boolean;
}) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      gap: 10,
      padding: "6px 0",
      borderBottom: "1px solid rgba(255,255,255,0.04)",
      fontSize: 13,
    }}
  >
    <span style={{ color: "var(--hint)" }}>{k}</span>
    <span
      style={{
        color: "var(--text)",
        textAlign: "right",
        wordBreak: "break-all",
        maxWidth: "70%",
        fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined,
        fontSize: mono ? 11 : 13,
      }}
    >
      {v}
    </span>
  </div>
);

const muted: React.CSSProperties = {
  color: "var(--hint)",
  fontSize: 13,
  lineHeight: 1.5,
  margin: 0,
};

const btn: React.CSSProperties = {
  marginTop: 12,
  width: "100%",
  padding: "12px 16px",
  borderRadius: 10,
  border: 0,
  background: "var(--accent, #F2C94C)",
  color: "var(--bg, #0E1116)",
  font: "inherit",
  fontWeight: 600,
  fontSize: 15,
  cursor: "pointer",
};

const input: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.1)",
  background: "var(--bg)",
  color: "var(--text)",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 13,
};
